import React, { useState, useEffect, useContext } from "react";
import {
  Shield,
  Heart,
  Plus,
  Edit,
  Trash2,
  X,
  Search,
  Calendar,
  User,
  Settings,
  ArrowLeft,
  ChevronRight,
  Info,
} from "lucide-react";
import translations from "../../../constants/translations";
import { apiFetch } from "../../../../../context/AuthContext";
import Mo3tiButton from "../../layout/Mo3tiButton";
import EmployeeListStep from "./EmployeeListStep";
import FamilyMembersStep from "./FamilyMembersStep";
import TiersPanel from "./TiersPanel";

const MedicalInsuranceModal = ({
  onClose,
  API_BASE_URL,
  clientName,
  workingCompany,
  theme,
  language,
}) => {
  const isDark = theme === "dark";
  const isArabic = language === "ar";
  const t = (key) =>
    translations[language]?.[key] || translations["en"]?.[key] || key;

  const currentTheme = {
    colors: {
      primary: "#2e7cba",
      success: "#10b981",
      danger: "#ef4444",
      warning: "#f59e0b",
      textPrimary: isDark ? "#ffffff" : "#1a1a1a",
      textSecondary: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
      border: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      bgCard: isDark ? "rgba(30, 41, 59, 0.7)" : "rgba(255, 255, 255, 0.9)",
    },
  };

  const modalStyles = {
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 3000,
      padding: "20px",
    },
    modalContent: {
      background: isDark ? "rgba(15, 23, 42, 0.95)" : "rgba(255, 255, 255, 0.98)",
      backdropFilter: "blur(20px)",
      border: `1px solid ${currentTheme.colors.border}`,
      borderRadius: "28px",
      padding: "32px",
      width: "100%",
      maxWidth: "1000px",
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
      boxShadow: isDark
        ? "0 25px 50px -12px rgba(0, 0, 0, 0.5)"
        : "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
      overflow: "hidden",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "24px",
      paddingBottom: "16px",
      borderBottom: `1px solid ${currentTheme.colors.border}`,
    },
    title: {
      color: currentTheme.colors.textPrimary,
      fontSize: "24px",
      fontWeight: "800",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    body: {
      flex: 1,
      overflowY: "auto",
      paddingRight: "8px",
      scrollbarWidth: "thin",
    },
    footer: {
      marginTop: "24px",
      paddingTop: "20px",
      borderTop: `1px solid ${currentTheme.colors.border}`,
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
    },
  };

  const [step, setStep] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(true);
  const [empSearch, setEmpSearch] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [members, setMembers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showTiersPanel, setShowTiersPanel] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  };

  const fetchEmployees = async () => {
    setLoadingEmps(true);
    try {
      const r = await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/employees?clientName=${encodeURIComponent(clientName)}&viewType=admin`,
      );
      const d = await r.json();
      setEmployees(d.employees || []);
    } catch (e) {
      setEmployees([]);
    } finally {
      setLoadingEmps(false);
    }
  };

  const fetchTiers = async () => {
    try {
      const params = new URLSearchParams({ clientName, includeInactive: "true" });
      if (workingCompany) params.set("workingCompany", workingCompany);
      const r = await apiFetch(`${API_BASE_URL}/payrollX/medical-insurance/tiers?${params}`);
      const d = await r.json();
      setTiers(d.tiers || []);
    } catch (e) {
      setTiers([]);
    }
  };

  const fetchMembers = async (empId) => {
    setLoadingMembers(true);
    try {
      const r = await apiFetch(
        `${API_BASE_URL}/payrollX/medical-insurance/employees/${encodeURIComponent(empId)}/members`,
      );
      const d = await r.json();
      setMembers(d.members || []);
    } catch (e) {
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchTiers();
  }, []);

  const handleSelectEmployee = (emp) => {
    setSelectedEmp(emp);
    setStep("members");
    fetchMembers(emp.infinityCode);
  };

  const handleBack = () => {
    setStep("employees");
    setSelectedEmp(null);
    setMembers([]);
  };

  const filteredEmployees = employees.filter((e) => {
    const q = empSearch.toLowerCase();
    const name = isArabic ? e.arabicName || e.name : e.name || e.arabicName;
    return (
      !q ||
      name.toLowerCase().includes(q) ||
      (e.infinityCode || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={modalStyles.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyles.modalContent}>
        <div style={modalStyles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {step === "members" && (
              <Mo3tiButton
                variant="ghost"
                onClick={handleBack}
                icon={ArrowLeft}
                style={{ padding: "8px", borderRadius: "12px" }}
              />
            )}
            <h1 style={modalStyles.title}>
              <Heart size={28} color={currentTheme.colors.danger} />
              {step === "employees" ? t("miManagement") : t("miFamilyMembers")}
            </h1>
          </div>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {step === "employees" && (
              <Mo3tiButton
                variant="secondary"
                onClick={() => setShowTiersPanel(!showTiersPanel)}
                icon={Settings}
              >
                {t("miManageTiers")}
              </Mo3tiButton>
            )}
            <Mo3tiButton
              variant="ghost"
              onClick={onClose}
              icon={X}
              style={{ padding: "8px", borderRadius: "12px" }}
            />
          </div>
        </div>

        <div style={modalStyles.body}>
          {showTiersPanel && step === "employees" ? (
            <TiersPanel
              tiers={tiers}
              fetchTiers={fetchTiers}
              API_BASE_URL={API_BASE_URL}
              clientName={clientName}
              workingCompany={workingCompany}
              currentTheme={currentTheme}
              t={t}
              isDark={isDark}
              getAuthHeaders={getAuthHeaders}
            />
          ) : step === "employees" ? (
            <EmployeeListStep
              loading={loadingEmps}
              employees={filteredEmployees}
              empSearch={empSearch}
              setEmpSearch={setEmpSearch}
              onSelect={handleSelectEmployee}
              currentTheme={currentTheme}
              t={t}
              isDark={isDark}
              isArabic={isArabic}
            />
          ) : (
            <FamilyMembersStep
              selectedEmp={selectedEmp}
              members={members}
              tiers={tiers}
              loading={loadingMembers}
              fetchMembers={() => fetchMembers(selectedEmp.infinityCode)}
              fetchEmployees={fetchEmployees}
              API_BASE_URL={API_BASE_URL}
              currentTheme={currentTheme}
              t={t}
              isDark={isDark}
              isArabic={isArabic}
              getAuthHeaders={getAuthHeaders}
            />
          )}
        </div>

        <div style={modalStyles.footer}>
          <Mo3tiButton variant="secondary" onClick={onClose}>
            {t("close")}
          </Mo3tiButton>
        </div>
      </div>
    </div>
  );
};

export default MedicalInsuranceModal;